import { keyframes } from '@mui/material/styles';
import { SxStyles } from '../types';
import { getResponsiveSize } from '../utils';

const startCallBreathing = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(32, 186, 143, 0));
  }
  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 0 ${getResponsiveSize(14)} rgba(32, 186, 143, 0.55));
  }
`;

const ldsEllipsisSlideLarge = keyframes`
  0%   { transform: translate(0, 0); }
  100% { transform: translate(${getResponsiveSize(27)}, 0); }
`;

export const PractitionerPageStyles: SxStyles = {
  mainGameSlot: {
    position: 'absolute',
    left: '50%',
    // Lifted above the call button overlay (which starts at bottom: 285) so the
    // bouncing ball can't dip onto the green call icon.
    bottom: getResponsiveSize(395),
    transform: 'translateX(-50%)',
    // 5% smaller than full-bleed: 95% → 90% width, 65vh → 62vh vertical clamp.
    // Footer/client-preview sizing is unchanged.
    width: 'min(90%, calc(62vh * 16 / 9))',
    zIndex: 4,
    pointerEvents: 'none',
    // 16:9 height via padding-top trick — more reliable across browsers than aspect-ratio
    // when combined with min()/max width clamps.
    '&::before': {
      content: '""',
      display: 'block',
      paddingTop: '56.25%',
    },
    '& canvas': {
      borderRadius: getResponsiveSize(12),
    },
    '&.game-promoted': {
      bottom: `max(${getResponsiveSize(265)}, calc(1% + 215px))`,
      width: 'min(85%, calc(62vh * 16 / 9))',
    },
    '&.child-theme': {
      backgroundImage: 'url(/assets/phaser/images/child_bg_emptylandscape.png)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: getResponsiveSize(12),
    },
  },

  preCallBanner: {
    position: 'absolute',
    left: 0,
    bottom: getResponsiveSize(530),
    width: '75%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getResponsiveSize(28),
    zIndex: 3,
    pointerEvents: 'none',
    textAlign: 'center',
  },

  preCallStatusMedia: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      width: getResponsiveSize(410),
      height: 'auto',
      display: 'block',
    },
  },

  headline: {
    margin: 0,
    fontSize: getResponsiveSize(28),
    fontWeight: 400,
    color: '#FFFFFF',
    letterSpacing: getResponsiveSize(0.15),
    lineHeight: 1.3,
  },

  sessionCodeInline: {
    marginLeft: getResponsiveSize(10),
    fontWeight: 700,
    letterSpacing: getResponsiveSize(1.5),
    textTransform: 'uppercase',
  },

  subline: {
    margin: 0,
    fontSize: getResponsiveSize(22),
    color: 'rgba(255, 255, 255, 0.72)',
    lineHeight: 1.4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getResponsiveSize(10),
  },

  copyLinkButton: {
    pointerEvents: 'auto',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    color: '#6EC1E4',
    fontSize: getResponsiveSize(14),
    fontWeight: 600,
    textDecoration: 'underline',
    fontFamily: 'inherit',
    '&:hover, &:focus': {
      color: '#8FD3F0',
      outline: 'none',
    },
  },

  copiedTag: {
    position: 'absolute',
    left: `calc(100% + ${getResponsiveSize(12)})`,
    top: '50%',
    transform: 'translateY(-50%)',
    whiteSpace: 'nowrap',
    color: '#20BA8F',
    fontSize: getResponsiveSize(13),
    fontWeight: 600,
  },

  copyLinkGroup: {
    order: 2,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: getResponsiveSize(80),
  },

  copyLinkCircleButton: {
    pointerEvents: 'auto',
    width: getResponsiveSize(80),
    height: getResponsiveSize(80),
    borderRadius: '50%',
    background: 'linear-gradient(-1deg, #517ec5 0%, #6bbbe1 100%)',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    outline: 'none',
    '&:hover': {
      opacity: 0.8,
    },
    '&:focus-visible': {
      boxShadow: '0 0 0 3px rgba(107, 187, 225, 0.5)',
    },
    '& img': {
      width: getResponsiveSize(32),
      height: getResponsiveSize(32),
    },
  },

  callButtonTextStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getResponsiveSize(6),
    textAlign: 'center',
  },

  callButtonOverlay: {
    position: 'absolute',
    left: 0,
    bottom: getResponsiveSize(285),
    width: '75%',
    height: getResponsiveSize(210),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: getResponsiveSize(30),
    zIndex: 3,
    pointerEvents: 'none',
    // Pre-call with the game promoted to the main area: drop the call button down
    // so the game has room above it. The `max(..., calc(1% + 130px))` floor keeps
    // it a fixed ~15px above the FIXED-height (115px) media controls bar — without
    // it, getResponsiveSize(160) shrinks on small windows and the button slides
    // behind the media row (which does not scale). 130 ≈ media bar (1% + 115) + gap.
    '&.game-promoted': {
      bottom: `max(${getResponsiveSize(160)}, calc(1% + 130px))`,
    },
    '& > button, & > .loading-button': {
      order: 2,
    },
    '& > button': {
      pointerEvents: 'auto',
      border: 0,
      padding: 0,
      outline: 0,
      cursor: 'pointer',
      background: 'transparent',
      lineHeight: 0,
      fontSize: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .call-button': {
      '&:hover, &:focus': { opacity: 0.8 },
      '& img': { height: getResponsiveSize(80), width: getResponsiveSize(80) },
    },
    '&[data-state="awaiting-start"] .call-button:not(.stop) img': {
      animation: `${startCallBreathing} 2s ease-in-out infinite`,
      transformOrigin: 'center center',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&[data-state="awaiting-start"] .call-button:not(.stop) img': {
        animation: 'none',
      },
    },
    '& .loading-button': {
      display: 'inline-block',
      position: 'relative',
      width: getResponsiveSize(90),
      height: getResponsiveSize(90),
      background: 'lightgrey',
      borderRadius: '50%',
      '& div': {
        position: 'absolute',
        top: getResponsiveSize(37.125),
        width: getResponsiveSize(14.625),
        height: getResponsiveSize(14.625),
        borderRadius: '50%',
        background: '#fff',
        animationTimingFunction: 'cubic-bezier(0, 1, 1, 0)',
      },
      '& div:nth-of-type(1)': { left: getResponsiveSize(9), animation: 'lds-ellipsis1 0.6s infinite' },
      '& div:nth-of-type(2)': { left: getResponsiveSize(9), animation: `${ldsEllipsisSlideLarge} 0.6s infinite` },
      '& div:nth-of-type(3)': { left: getResponsiveSize(36), animation: `${ldsEllipsisSlideLarge} 0.6s infinite` },
    },
  },
};
